import React, { useMemo } from 'react';
import { Spinner } from '@/components/ui/spinner';
import { PORTAL_TARGETS } from '@/types/shared/navigation-tabs.enums';
import { useFetchFaults, useFaultsTable, usePortal } from '@/hooks';
import { useTranslation } from 'react-i18next';
import { BaseTable } from '@/modules';
import { useFaultsData } from '@/hooks/AssetsPreviewPage/shared/useFaultsData';
import { useSearchByHeader } from '@/components/uiKit/Tables/useSearchByHeader';
import { BaseDrawer } from '@/components';
import { ItemsDrawerContent } from './components/ItemsDrawerContent';
import { useItemsDrawer } from './hooks/useItemsDrawer';
import { applyFilters } from '@/components/uiKit/FiltersGroup/utils';
import { faultsFilterConfig } from '@/store/slices/assetsPreviewSlice/faults-filter-config';
import { Icon } from '@/components/shared/display/Icon';
import { Success } from '@/assets/Icons';
import { TableView } from '../../AssetPreviewViews';
import { useSelector } from 'react-redux';
import { Fault } from '@/types/models/faults.types';
import { RootState } from '@/store/store';
import { getVisibleColumns } from '@/utils/table';

const EmptyState = {
  NONE: 'NONE',
  NO_DATA: 'NO_DATA',
  NO_SEARCH_RESULTS: 'NO_SEARCH_RESULTS',
  NO_FILTER_RESULTS: 'NO_FILTER_RESULTS',
};

export const Faults = () => {
  const { faultsCurrentFiltersValues: currentFiltersValues, faultsInitialFiltersValues: initialFiltersValues } =
    useSelector((state: RootState) => state.assetsPreview);
  const { isLoading, isFetching, faultsRaw: faults, error } = useFetchFaults();
  const { render } = usePortal(PORTAL_TARGETS.ASSETS_SEARCH_CONTAINER);

  const { tableStructure: faultsColumns } = useFaultsTable();

  // Filter out columns that have hideWhenEmpty meta and no data in any row
  const visibleColumns = useMemo(() => getVisibleColumns(faultsColumns, faults || []), [faultsColumns, faults]);

  const filteredFaultsBySearch = useFaultsData(faults);
  const search = useSearchByHeader(visibleColumns, true);

  const filteredFaults = useMemo(() => {
    if (!currentFiltersValues) return null;
    return applyFilters(filteredFaultsBySearch, faultsFilterConfig, currentFiltersValues);
  }, [currentFiltersValues, filteredFaultsBySearch]);

  const afterSearchValues = filteredFaultsBySearch;

  // Check if user has changed filters from their initial state
  const hasUserChangedFilters = useMemo(() => {
    if (!currentFiltersValues || !initialFiltersValues) return false;
    return JSON.stringify(currentFiltersValues) !== JSON.stringify(initialFiltersValues);
  }, [currentFiltersValues, initialFiltersValues]);

  // Use filtered faults when user has actively changed filters, otherwise use search results
  const tableFaults: Fault[] = useMemo(() => {
    // User changed filters - use filtered results
    if (hasUserChangedFilters && filteredFaults !== null) {
      return filteredFaults;
    }
    // Filters not changed - if filtered has results use it, otherwise use search results
    if (filteredFaults !== null && filteredFaults.length > 0) {
      return filteredFaults;
    }
    return afterSearchValues || [];
  }, [hasUserChangedFilters, filteredFaults, afterSearchValues]);

  // Determine empty state - only relevant when tableFaults is empty
  const getEmptyState = () => {
    // If we have data to show, no empty state needed
    if (tableFaults.length > 0) {
      return EmptyState.NONE;
    }
    // No raw data at all
    if (!faults?.length || error) {
      return EmptyState.NO_DATA;
    }
    // Search was applied but returned no results
    if (afterSearchValues && afterSearchValues.length === 0) {
      return EmptyState.NO_SEARCH_RESULTS;
    }
    // User changed filters and they returned no results
    if (hasUserChangedFilters && filteredFaults !== null && filteredFaults.length === 0) {
      return EmptyState.NO_FILTER_RESULTS;
    }
    return EmptyState.NONE;
  };

  const emptyState = getEmptyState();

  const renderEmptyState = () => {
    switch (emptyState) {
      case EmptyState.NO_DATA:
        return {
          icon: <Icon icon={Success} className="mb-2 w-14 h-14" />,
          message: (
            <>
              <div>No Open Faults</div>
            </>
          ),
        };

      case EmptyState.NO_SEARCH_RESULTS:
        return {
          message: (
            <>
              <div>No results for {search.inputOnlyValue}</div>
              <div>Please make sure the name is spelled correctly</div>
            </>
          ),
        };

      case EmptyState.NO_FILTER_RESULTS:
        return {
          message: (
            <>
              <div>No faults found</div>
              <div>Try adjusting your filters or search terms</div>
            </>
          ),
        };

      default:
        return undefined;
    }
  };

  const { isOpen, view, openDrawer, openDetail, closeDrawer } = useItemsDrawer(tableFaults);

  // Get selected row ID from the view
  const selectedFaultId = view?.type === 'DETAIL' ? view.itemId : null;

  return (
    <>
      <div className="flex-col gap-4 h-full flex">
        {isLoading || isFetching ? (
          <>
            {render(<div className="invisible h-[40px]" />)}
            <div
              className="relative flex items-center justify-center bg-white/80 rounded border border-gray-200"
              style={{ height: 'calc(100vh - 90px)' }}
            >
              <div className="absolute inset-0 bg-white/60 rounded" />
              <div className="relative z-10">
                <Spinner size="lg" />
              </div>
            </div>
          </>
        ) : (
          <BaseDrawer
            rounded={false}
            isOpen={isOpen}
            onClose={closeDrawer}
            className="border-0 border-l border-neutral-100 min-w-[40vw] w-[40vw]"
            title="Fault Details"
            showHeaderDivider
            direction="right"
            content={
              <ItemsDrawerContent
                items={tableFaults}
                drawerView={view}
                onOpenDetail={openDetail}
                isLoading={isLoading}
              />
            }
          >
            <TableView<Fault>
              emptyStateMessage={renderEmptyState()}
              columns={visibleColumns}
              items={tableFaults}
              renderSearchInto={PORTAL_TARGETS.ASSETS_SEARCH_CONTAINER}
              rowClickHandler={openDrawer}
              selectedRowId={selectedFaultId}
              getRowId={(fault) => fault.fault_id}
            />
          </BaseDrawer>
        )}
      </div>
    </>
  );
};
